import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ELMUJIB_DEFAULT_BASE_URL,
  META_CHANNEL_KIND_DEFS,
  META_CHANNEL_KIND_MAP,
  META_PROVIDER_DEFS,
  missingSetup,
  type ConnectChannelInput,
  type MetaChannelKind,
  type MetaChannelView,
  type MetaProvider,
  type MetaStatus,
} from '@goyone/shared';
import { api } from '../../lib/api';
import { dateTime, relative } from '../../lib/format';
import { Icon } from '../../components/Icon';
import { Badge, EmptyState, ErrorState, Modal, PageHead } from '../../components/ui';
import { Menu } from '../../components/Menu';
import { useToast } from '../../components/Toast';

/**
 * إعدادات قنوات ميتا.
 *
 * ## الشاشة تقول ما ينقص بالضبط
 *
 * لا «غير متصل» وحدها: من يقرؤها يريد أن يعرف ماذا يفعل. وشاشةٌ تقول
 * «متصل» وهي ليست كذلك أسوأ — تجعل من يعتمد عليها يفقد رسائل عملاء ولا
 * يدري.
 *
 * ## والاستقبال يعمل قبل الإرسال
 *
 * الوارد يحتاج سرّ التطبيق ورمز التثبيت — وكلاهما في بيئة التشغيل. والصادر
 * يحتاج رمز وصولٍ لكل قناة، يُنشأ في لوحة ميتا. فالحالتان مفصولتان في
 * العرض: قناةٌ تستقبل ولا تُرسل حالةٌ مشروعة لا عطل.
 */
export function MetaChannels() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [connecting, setConnecting] = useState(false);
  const [editing, setEditing] = useState<MetaChannelView | null>(null);

  const status = useQuery({
    queryKey: ['meta', 'status'],
    queryFn: () => api<MetaStatus>('/meta/status'),
  });

  const channels = useQuery({
    queryKey: ['meta', 'channels'],
    queryFn: () => api<MetaChannelView[]>('/meta/channels'),
  });

  const toggle = useMutation({
    mutationFn: (c: MetaChannelView) =>
      api(`/meta/channels/${c.id}`, { method: 'PATCH', body: { isActive: !c.isActive } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['meta'] });
      toast('حُدّثت القناة');
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر التحديث.'),
  });

  const disconnect = useMutation({
    mutationFn: (id: string) => api(`/meta/channels/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['meta'] });
      toast('فُكّ ربط القناة');
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر فكّ الربط.'),
  });

  const copyWebhook = async () => {
    const url = status.data?.webhookUrl ?? '';
    try {
      await navigator.clipboard.writeText(url);
      toast('نُسخ عنوان الخطّاف');
    } catch {
      window.prompt('انسخ عنوان الخطّاف:', url);
    }
  };

  const rows = channels.data ?? [];
  const gaps = status.data ? missingSetup(status.data) : [];

  return (
    <div className="page">
      <PageHead
        title="قنوات ميتا"
        subtitle="واتساب وماسنجر وإنستجرام — رسائل العملاء داخل النظام"
        actions={
          <button
            className="btn btn-primary"
            onClick={() => setConnecting(true)}
            disabled={!status.data}
          >
            <Icon name="plus" size={15} />
            اربط قناة
          </button>
        }
      />

      {/* ---------- حالة الربط ---------- */}
      {status.isError && <ErrorState error={status.error} onRetry={() => void status.refetch()} />}

      {status.data && (
        <div className="card mb-4">
          <div className="card-head">
            <h2 className="card-title">حالة الاستقبال</h2>
            <Badge kind={status.data.canReceive ? 'success' : 'warning'}>
              {status.data.canReceive ? 'جاهز لاستقبال الرسائل' : 'غير جاهز'}
            </Badge>
          </div>

          <div className="card-body">
            {gaps.length > 0 ? (
              <>
                <p className="small mb-2">
                  ينقص ضبطُ الآتي في بيئة تشغيل الخادم (ملف <code>.env</code>):
                </p>
                <ul className="meta-gaps">
                  {gaps.map((g) => (
                    <li key={g} className="small">
                      <Icon name="alert" size={14} />
                      {g}
                    </li>
                  ))}
                </ul>
                {/*
                  لا زرّ «اربط الآن» هنا.
                  القيم تُضبط في بيئة الخادم لا من الشاشة، وزرٌّ يوهم بغير
                  ذلك يجعل المسؤول ينقر ثم لا يفهم لماذا لم يتغيّر شيء.
                */}
                <p className="tiny mute mt-3">
                  اضبطها ثم أعِد تشغيل الخادم. الاستقبال يعمل بعدها بلا خطوة أخرى.
                </p>
              </>
            ) : (
              <p className="small">
                الخادم يقبل خطّافات ميتا ويتحقّق من توقيعها. ضع العنوان التالي في لوحة
                ميتا تحت «Webhooks».
              </p>
            )}

            <div className="meta-webhook mt-3">
              <code dir="ltr">{status.data.webhookUrl}</code>
              <button className="btn btn-sm" onClick={() => void copyWebhook()}>
                <Icon name="send" size={14} />
                انسخ
              </button>
            </div>

            {/* خطّاف المجيب — رابطٌ آخر بسرٍّ في ذيله. */}
            <div className="mt-4">
              <p className="small mb-2">
                <strong>لقنوات المجيب:</strong> ضع هذا العنوان في المجيب تحت «Settings ← API
                Access & Webhook».
                {!status.data.elmujibReady && (
                  <span className="tiny mute">
                    {' '}
                    (اضبط <code>ELMUJIB_WEBHOOK_SECRET</code> في <code>.env</code> وأعِد تشغيل الخادم
                    ليعمل الاستقبال.)
                  </span>
                )}
              </p>
              <div className="meta-webhook">
                <code dir="ltr">{status.data.elmujibWebhookUrl}</code>
                <button
                  className="btn btn-sm"
                  onClick={() => {
                    void navigator.clipboard
                      ?.writeText(status.data!.elmujibWebhookUrl)
                      .then(() => toast('نُسخ رابط خطّاف المجيب'))
                      .catch(() => undefined);
                  }}
                >
                  <Icon name="send" size={14} />
                  انسخ
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ---------- القنوات ---------- */}
      {channels.isError && (
        <ErrorState error={channels.error} onRetry={() => void channels.refetch()} />
      )}

      {channels.data && rows.length === 0 && (
        <EmptyState
          title="لا قنوات مربوطة"
          text="اربط رقم واتساب أو صفحة فيسبوك بمعرّفها من لوحة ميتا، لتصل رسائل العملاء هنا."
        />
      )}

      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>القناة</th>
                <th>النوع</th>
                <th>المعرّف في ميتا</th>
                <th>الاستقبال</th>
                <th>الإرسال</th>
                <th>آخر حدث</th>
                <th>المحادثات</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id}>
                  <td>
                    <strong>{c.name}</strong>
                  </td>
                  <td>
                    <Badge kind={META_CHANNEL_KIND_MAP[c.kind].kind}>
                      {META_CHANNEL_KIND_MAP[c.kind].label}
                    </Badge>
                  </td>
                  <td className="tiny" dir="ltr">
                    {c.externalId}
                  </td>
                  <td>
                    <Badge kind={c.isActive ? 'success' : 'neutral'}>
                      {c.isActive ? 'يعمل' : 'موقوف'}
                    </Badge>
                  </td>
                  <td>
                    {/*
                      الإرسال حالةٌ مستقلة عن الاستقبال.
                      قناةٌ تستقبل ولا تُرسل مشروعة — الوارد لا يحتاج رمزًا.
                    */}
                    <Badge kind={c.canSend ? 'success' : 'warning'}>
                      {c.canSend ? 'رمز محفوظ' : 'بلا رمز'}
                    </Badge>
                  </td>
                  <td className="tiny" title={c.lastEventAt ? dateTime(c.lastEventAt) : undefined}>
                    {c.lastEventAt ? relative(c.lastEventAt) : 'لم يصل شيء بعد'}
                  </td>
                  <td className="num">{c.conversationCount}</td>
                  <td>
                    <Menu
                      label={`إجراءات ${c.name}`}
                      actions={[
                        {
                          key: 'edit',
                          label: c.canSend ? 'بدّل رمز الوصول' : 'أضِف رمز وصول',
                          icon: 'edit',
                          onSelect: () => setEditing(c),
                        },
                        {
                          key: 'toggle',
                          label: c.isActive ? 'أوقف الاستقبال' : 'استأنف الاستقبال',
                          icon: 'refresh',
                          onSelect: () => toggle.mutate(c),
                        },
                        {
                          key: 'remove',
                          label: 'فكّ الربط',
                          icon: 'trash',
                          danger: true,
                          separated: true,
                          /*
                            فكّ الربط يُسقط المحادثات معه.
                            والإيقاف بديلٌ يحفظها — فيُقيَّد الحذف بمن لا
                            محادثات له، ويُوجَّه الباقي إلى الإيقاف.
                          */
                          disabled: c.conversationCount > 0,
                          title:
                            c.conversationCount > 0
                              ? 'عليها محادثات تسقط معها. أوقف الاستقبال بدلًا من ذلك.'
                              : undefined,
                          onSelect: () => disconnect.mutate(c.id),
                        },
                      ]}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(connecting || editing) && (
        <ChannelModal
          channel={editing}
          onClose={() => {
            setConnecting(false);
            setEditing(null);
          }}
          onSaved={() => {
            setConnecting(false);
            setEditing(null);
            void queryClient.invalidateQueries({ queryKey: ['meta'] });
          }}
        />
      )}
    </div>
  );
}

/* ==========================================================================
   ربط قناة
   ========================================================================== */

function ChannelModal({
  channel,
  onClose,
  onSaved,
}: {
  channel: MetaChannelView | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [kind, setKind] = useState<MetaChannelKind>(channel?.kind ?? 'WHATSAPP');
  const [name, setName] = useState(channel?.name ?? '');
  const [externalId, setExternalId] = useState(channel?.externalId ?? '');
  const [accessToken, setAccessToken] = useState('');
  const [provider, setProvider] = useState<MetaProvider>(channel?.provider ?? 'META');
  const [baseUrl, setBaseUrl] = useState(channel?.providerBaseUrl ?? ELMUJIB_DEFAULT_BASE_URL);
  const [vendorId, setVendorId] = useState(channel?.providerVendorId ?? '');
  const [error, setError] = useState<string | null>(null);

  /* المزوّد يخصّ الواتساب؛ غيره على ميتا مباشرةً. */
  const isElmujib = kind === 'WHATSAPP' && provider === 'ELMUJIB';

  const save = useMutation({
    mutationFn: () => {
      if (channel) {
        return api(`/meta/channels/${channel.id}`, {
          method: 'PATCH',
          body: {
            name: name.trim(),
            ...(accessToken.trim() ? { accessToken: accessToken.trim() } : {}),
            ...(channel.provider === 'ELMUJIB'
              ? { providerBaseUrl: baseUrl.trim(), providerVendorId: vendorId.trim() }
              : {}),
          },
        });
      }
      const body: ConnectChannelInput = {
        kind,
        name: name.trim(),
        externalId: externalId.trim(),
        provider,
        ...(accessToken.trim() ? { accessToken: accessToken.trim() } : {}),
        ...(isElmujib
          ? { providerBaseUrl: baseUrl.trim(), providerVendorId: vendorId.trim() }
          : {}),
      };
      return api('/meta/channels', { method: 'POST', body });
    },
    onSuccess: () => {
      toast(channel ? 'حُدّثت القناة' : 'رُبطت القناة');
      onSaved();
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'تعذّر الحفظ.'),
  });

  const def = META_CHANNEL_KIND_MAP[kind];

  return (
    <Modal title={channel ? `تعديل «${channel.name}»` : 'ربط قناة ميتا'} onClose={onClose}>
      {error && <div className="alert alert-danger mb-4">{error}</div>}

      {!channel && (
        <div className="field">
          <span className="label">النوع</span>
          <div className="meta-kinds">
            {META_CHANNEL_KIND_DEFS.map((k) => (
              <label key={k.key} className="meta-kind">
                <input
                  type="radio"
                  name="meta-kind"
                  checked={kind === k.key}
                  onChange={() => setKind(k.key)}
                />
                <span>
                  <strong className="small">{k.label}</strong>
                  <span className="tiny mute">{k.needs}</span>
                </span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* المزوّد — للواتساب فقط، وعند الربط فقط. */}
      {!channel && kind === 'WHATSAPP' && (
        <div className="field">
          <span className="label">المزوّد</span>
          <div className="meta-kinds">
            {META_PROVIDER_DEFS.map((p) => (
              <label key={p.key} className="meta-kind">
                <input
                  type="radio"
                  name="meta-provider"
                  checked={provider === p.key}
                  onChange={() => setProvider(p.key)}
                />
                <span>
                  <strong className="small">{p.label}</strong>
                  <span className="tiny mute">{p.hint}</span>
                </span>
              </label>
            ))}
          </div>
        </div>
      )}

      <label className="field">
        <span className="label">اسم يميّزها عندك</span>
        <input
          className="input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="واتساب المبيعات"
        />
      </label>

      {/* إعدادات المجيب: الرابط ومعرّف المورّد. */}
      {isElmujib && (!channel || channel.provider === 'ELMUJIB') && (
        <>
          <label className="field">
            <span className="label">رابط واجهة المجيب</span>
            <input
              className="input"
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
              placeholder={ELMUJIB_DEFAULT_BASE_URL}
              dir="ltr"
            />
          </label>
          <label className="field">
            <span className="label">معرّف المورّد (Vendor UID)</span>
            <input
              className="input"
              value={vendorId}
              onChange={(e) => setVendorId(e.target.value)}
              placeholder="ace29e60-aeed-4163-9178-29c3383a38c6"
              dir="ltr"
            />
          </label>
        </>
      )}

      {!channel && (
        <label className="field">
          <span className="label">{isElmujib ? 'معرّف الرقم المُرسِل (Phone Number ID)' : 'المعرّف في ميتا'}</span>
          <input
            className="input"
            value={externalId}
            onChange={(e) => setExternalId(e.target.value)}
            placeholder="123456789012345"
            dir="ltr"
          />
          <span className="tiny mute">
            {isElmujib ? 'من لوحة المجيب: from_phone_number_id للرقم الذي تُرسل منه — أرقام فقط.' : def.needs}
          </span>
        </label>
      )}

      <label className="field">
        <span className="label">
          {isElmujib ? 'مفتاح وصول المجيب (API Access Token)' : 'رمز الوصول'}{' '}
          {channel?.canSend ? '(محفوظ — اتركه فارغًا لتُبقيه)' : isElmujib ? '' : '(اختياري)'}
        </span>
        <input
          className="input"
          type="password"
          value={accessToken}
          onChange={(e) => setAccessToken(e.target.value)}
          placeholder={isElmujib ? 'hLS0kzKQ…' : 'EAAG…'}
          dir="ltr"
          autoComplete="off"
        />
        <span className="tiny mute">
          {isElmujib
            ? 'مفتاح المجيب — يُشفَّر عند الحفظ ولا يُعرض بعدها. بدونه لا إرسال.'
            : 'بدونه تستقبل القناة ولا تُرسل. يُشفَّر عند الحفظ ولا يُعرض بعدها.'}
        </span>
      </label>

      <div className="row mt-4" style={{ gap: 'var(--sp-2)' }}>
        <button
          className="btn btn-primary"
          onClick={() => {
            setError(null);
            save.mutate();
          }}
          disabled={save.isPending || !name.trim() || (!channel && !externalId.trim())}
        >
          {save.isPending ? 'جارٍ الحفظ' : channel ? 'حفظ' : 'اربط'}
        </button>
        <button className="btn" onClick={onClose}>
          إلغاء
        </button>
      </div>
    </Modal>
  );
}
